import { type EventId } from '@solinkcorp/api-types';
import { useUserSession } from '@solinkcorp/authentication';
import { toast } from '@solinkcorp/solink-components';
import { EVENT_TYPE_BOOKMARK } from '@solinkcorp/vd-common';
import { type IEvent } from 'clients/PlatformApi';
import moment from 'moment';
import { useCallback } from 'react';
import useFluxStore from 'src/hooks/useFluxStore';
import useLocale from 'src/hooks/useLocale';
import usePlatformClient from 'src/hooks/usePlatformClient';
import { LocationStore } from 'stores/LocationStore';
import { uuidv4 } from 'utils/AlarmContactUtils';
import {
  EXCLUDE_FROM_COUNT_TAG,
  HIDE_FROM_DISPLAY_TAG,
  SCHEDULED_AI_CHAT_REASON,
} from 'vd-constants';
import { type NewActivityType } from './useScheduledEventForm';

function usePreviewSpotCheck() {
  const locale = useLocale();
  const user = useUserSession();
  const locations = useFluxStore(LocationStore, (store) => store.getLocationsById());
  const PlatformApi = usePlatformClient();

  const createSpotCheckPreview = useCallback(
    async (activity: NewActivityType, cameras: { [key: string]: any }) => {
      const location = activity?.siteId && locations?.[activity.siteId];

      if (!location) {
        toast.error(locale.general.unexpectedError);
        return;
      }

      const title = `${activity.title} - ${locale.scheduledAIChat.spotCheck} ${locale.general.preview}`;

      /** id returned to consumer for polling */
      const id = uuidv4() as EventId;

      const payload: Partial<IEvent> = {
        id,
        type: EVENT_TYPE_BOOKMARK,
        subtype: SCHEDULED_AI_CHAT_REASON,
        startTimeLocal: moment()
          .tz(location.timezone ?? '')
          .toDate(),
        endTimeLocal: moment()
          .tz(location.timezone ?? '')
          .toDate(),
        title,
        details: {
          scheduledActivity: {
            title,
            reason: SCHEDULED_AI_CHAT_REASON,
            metadata: {
              aiChatPrompt: activity.metadata?.aiChatPrompt,
              aiChatAnswerFormat: activity.metadata?.aiChatAnswerFormat ?? undefined,
              aiChatAnswerStates: activity.metadata?.aiChatAnswerStates,
              cameras: Object.values(cameras).map((camera: any) => ({ chId: camera.chId })),
            },
            schedule: activity.rule,
            reoccurringCount: 1,
          },
          generatedByEventStandard: true,
        },
        tags: [EXCLUDE_FROM_COUNT_TAG, HIDE_FROM_DISPLAY_TAG],
        isAlarm: false,
        startTime: new Date(),
        endTime: new Date(),
        locationId: activity?.deviceId,
        siteId: activity?.siteId,
        subtitle: `${locale.scheduledAIChat.spotCheck} - ${locale.general.preview}`,
        customerId: user.customerId,
        creator: user.userId,
      };

      await PlatformApi.createEventIngest(payload, true);

      return id;
    },
    [user, locations, PlatformApi, locale],
  );

  return {
    createSpotCheckPreview,
  };
}

export default usePreviewSpotCheck;
